import { useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { createEntityComponent } from "@coltorapps/builder-react";
import { buttonUrlEntity } from "./definition";
import { cn } from "@/utils/cn";
import { useTranslation } from "react-i18next";
import { useResolvedTargetUrl } from "./useResolvedTargetUrl";
import { useDisableExpressionExecution } from "../../expression-execution-context";
import { useAtomValue } from "jotai";
import { runtimeApplicationAtom } from "@/store";
import { registerFormCommit } from "@/utils/form-commit-registry";

export const ButtonUrlEntity = createEntityComponent(
  buttonUrlEntity,
  function ButtonUrlEntity(props) {
    const { t } = useTranslation();
    const label = !!props.entity.attributes.label.value
      ? props.entity.attributes.label.value
      : props.entity.attributes.name;

    const buttonText = props.entity.attributes.buttonText?.trim()
      ? props.entity.attributes.buttonText
      : "Open";

    const isButton = props.entity.attributes.isButton !== false; // default true if not false
    const openNewTab = props.entity.attributes.openNewTab !== false;
    const disableExpressionExecution = useDisableExpressionExecution();
    const runtimeApplication = useAtomValue(runtimeApplicationAtom);
    const currentUrlValue =
      typeof props.entity.value === "string" ? props.entity.value : undefined;
    const formDataUrl =
      runtimeApplication?.formInstance.data?.[props.entity.id] ??
      runtimeApplication?.formInstance.data?.[props.entity.attributes.name];
    const persistedUrlValue =
      typeof formDataUrl === "string" ? formDataUrl : undefined;
    const shouldResolveUrl =
      !disableExpressionExecution &&
      (!props.entity.attributes.readonly || !runtimeApplication);
    const {
      mode,
      fixedUrl,
      resolvedUrl,
      resolveTargetUrl,
      errorKey,
      isResolving,
    } = useResolvedTargetUrl(props.entity.attributes.targetUrl, {
      disabled: !shouldResolveUrl,
    });
    const computedUrl =
      mode === "expression" ? resolvedUrl || undefined : fixedUrl || undefined;
    const snapshotUrl = currentUrlValue || persistedUrlValue;
    const targetUrl =
      (shouldResolveUrl
        ? computedUrl || snapshotUrl
        : snapshotUrl || computedUrl) || "#";
    const errorMessage = errorKey ? t(errorKey) : undefined;
    const commitUrlValue = useCallback(
      (nextUrl: string) => {
        props.setValue(nextUrl);
      },
      [props.setValue],
    );

    useEffect(() => {
      if (!shouldResolveUrl) return;

      const nextValue =
        mode === "expression"
          ? resolvedUrl || undefined
          : fixedUrl || undefined;
      if (nextValue && currentUrlValue !== nextValue) {
        commitUrlValue(nextValue);
      }
    }, [
      currentUrlValue,
      commitUrlValue,
      fixedUrl,
      mode,
      resolvedUrl,
      shouldResolveUrl,
    ]);

    useEffect(() => {
      return registerFormCommit(async () => {
        if (mode === "fixed") {
          const nextUrl = fixedUrl || undefined;
          if (nextUrl && currentUrlValue !== nextUrl) {
            commitUrlValue(nextUrl);
          }
          return;
        }

        if (!shouldResolveUrl || runtimeApplication?.reviewStatus) {
          return;
        }

        const nextUrl =
          resolvedUrl || (await resolveTargetUrl({ force: true }));
        if (nextUrl && currentUrlValue !== nextUrl) {
          commitUrlValue(nextUrl);
        }
      });
    }, [
      currentUrlValue,
      commitUrlValue,
      fixedUrl,
      mode,
      resolveTargetUrl,
      resolvedUrl,
      runtimeApplication?.reviewStatus,
      shouldResolveUrl,
    ]);

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (props.entity.attributes.disabled) return;
      if (targetUrl === "#") return;
      if (mode === "expression" && shouldResolveUrl && !snapshotUrl) {
        if (isResolving || !resolvedUrl) {
          return;
        }
        window.open(
          resolvedUrl,
          openNewTab ? "_blank" : "_self",
          openNewTab ? "noopener,noreferrer" : undefined,
        );
        return;
      }

      if (targetUrl) {
        window.open(
          targetUrl,
          openNewTab ? "_blank" : "_self",
          openNewTab ? "noopener,noreferrer" : undefined,
        );
      }
    };

    return (
      <div className="w-full">
        <Label aria-required={props.entity.attributes.required}>{label}</Label>
        <div className="mt-1">
          {isButton ? (
            <Button
              type="button"
              variant="tertiary"
              onClick={handleClick}
              disabled={
                props.entity.attributes.disabled ||
                (mode === "expression" &&
                  shouldResolveUrl &&
                  !snapshotUrl &&
                  isResolving)
              }
              aria-label={`Open ${label}`}
            >
              {buttonText}
            </Button>
          ) : (
            <a
              href={targetUrl}
              onClick={handleClick}
              className={cn(
                "text-center justify-center text-giant-blue text-base font-medium underline",
                (props.entity.attributes.disabled ||
                  (mode === "expression" &&
                    shouldResolveUrl &&
                    !snapshotUrl &&
                    isResolving)) &&
                  "text-secondary-text cursor-not-allowed",
              )}
              aria-label={`Open ${label}`}
              tabIndex={0}
            >
              {buttonText}
            </a>
          )}
        </div>
        {mode === "expression" && errorMessage ? (
          <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
        ) : null}
      </div>
    );
  },
);
